import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #refreshController = null;
  #cartRevision = 0;
  /** @type {Set<Promise<unknown>>} */
  #pendingUpdates = new Set();
  #refreshQueued = false;

  // Cart mutations invalidate any earlier read before their responses can render.
  #handleCartMutation = (event) => {
    this.#cartRevision++;
    if (this.#refreshController) this.#refreshQueued = true;
    this.#refreshController?.abort();
    this.#refreshController = null;
    if (!event.promise || this.#pendingUpdates.has(event.promise)) return;
    const promise = event.promise;
    this.#pendingUpdates.add(promise);
    const settled = () => {
      this.#pendingUpdates.delete(promise);
      if (this.#refreshQueued && this.#pendingUpdates.size === 0 && this.isConnected) {
        this.#refreshQueued = false;
        this.#refreshCart();
      }
    };
    promise.then(settled, settled);
  };

  #handlePageShow = (event) => {
    if (event.persisted) this.#refreshCart();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#refreshCart();
  };

  /** Read the current session without changing its cart or emitting mutation analytics. */
  #refreshCart = async () => {
    if (this.#pendingUpdates.size > 0) {
      this.#refreshQueued = true;
      return;
    }
    if (this.#refreshController) return;

    const controller = new AbortController();
    this.#refreshController = controller;
    const revision = this.#cartRevision;
    const sectionsURL = new URL(window.location.href);
    sectionsURL.searchParams.delete('sections');
    sectionsURL.searchParams.set('section_id', 'cart-drawer-section');
    const options = {
      credentials: /** @type {RequestCredentials} */ ('same-origin'),
      cache: /** @type {RequestCache} */ ('no-store'),
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    };

    try {
      const [cartResponse, sectionsResponse] = await Promise.all([
        fetch(`${Theme.routes.cart_url}.js`, options),
        fetch(sectionsURL, { ...options, headers: { Accept: 'text/html' } }),
      ]);
      if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('Cart refresh failed');
      const [cart, html] = await Promise.all([cartResponse.json(), sectionsResponse.text()]);
      if (!html) throw new Error('Cart drawer section missing');
      if (controller.signal.aborted || revision !== this.#cartRevision || !this.isConnected) return;

      await morphSection('cart-drawer-section', html, { mode: 'hydration', injectStylesheet: true });
      if (controller.signal.aborted || revision !== this.#cartRevision || !this.isConnected) return;
      for (const icon of document.querySelectorAll('cart-icon')) {
        /** @type {HTMLElement & { renderCartBubble?: (count: number, animate: boolean) => void }} */ (icon)
          .renderCartBubble?.(cart.item_count, false);
      }
      this.#updateStickyState();
    } catch (error) {
      // A failed read must leave the last known cart intact, never turn it into an empty cart.
      if (!controller.signal.aborted) console.warn('[cart-drawer] Refresh failed:', error);
    } finally {
      if (this.#refreshController === controller) this.#refreshController = null;
    }
  };

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartMutation);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartMutation);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleCartMutation);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
    this.#refreshCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#refreshController?.abort();
    this.#refreshController = null;
    this.#refreshQueued = false;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartMutation);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartMutation);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleCartMutation);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#refreshCart();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
